import { CommandBar, ICommandBarItemProps, Slider, Text } from "@fluentui/react";
import { useEffect, useState } from "react";
import InfoList from "./InfoList";
import { Area, Building, EntityQuery, Filter, LandUse, LandUseI18N, LocalGovernment, Parcel, PlotOfLand, PostalCode, Query, getGeoArea } from "@the-real-insight/tri-model";
import { userAPI } from "../UserAPI";
import SocioDemographicsProperties from "./plot-of-land/SocioDemographicsProperties";
import DecarbonizationProperties from "./plot-of-land/DecarbonisationProperties";
import PoIProperties from "./plot-of-land/PoIProperties";
import * as turf from '@turf/turf';

class Properties {
  parcel?: Parcel;
  poisChanged?: (pois: any[]) => void;
}

const numberFormat = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export default function ParcelProperties(properties: Properties) {
  const [tab, setTab] = useState('general');
  const [postalCode, setPostalCode] = useState() as any;
  const [area, setArea] = useState(0);

  const _items: ICommandBarItemProps[] = [{
    key: 'general',
    text: 'Allgemein',
    iconProps: { iconName: 'Location' },
    onClick: () => setTab('general'),
  }/*, {
    key: 'buildings',
    text: 'Bebauung',
    iconProps: { iconName: 'EMI' },
    onClick: () => setTab('buildings'),
  }, {
    key: 'sociodemographics',
    text: 'Soziodemographie',
    iconProps: { iconName: 'SchoolDataSyncLogo' },
    onClick: () => setTab('sociodemographics'),
  }, {
    key: 'crime',
    text: 'Kriminalität',
    iconProps: { iconName: 'EndPointSolid' },
    onClick: () => setTab('crime'),
  }, {
    key: 'poi',
    text: 'In der Nähe',
    iconProps: { iconName: 'SchoolDataSyncLogo' },
    onClick: () => setTab('poi'),
  }, {
    key: 'topology',
    text: 'Beschaffenheit',
    iconProps: { iconName: 'MountainClimbing' },
    onClick: () => setTab('topology'),
  }, {
    key: 'weather',
    text: 'Wetter',
    iconProps: { iconName: 'CloudWeather' },
    onClick: () => setTab('weather'),
  }, {
    key: 'project',
    text: 'Projekt',
    iconProps: { iconName: 'ExternalBuild' },
    onClick: () => setTab('project'),
  }, {
    key: 'ownership',
    text: 'Eigentumsverhältnisse',
    iconProps: { iconName: 'Compare' },
    onClick: () => setTab('ownership'),
  }, {
    key: 'energyManagement',
    text: 'Energiemanagment',
    iconProps: { iconName: 'Lightbulb' },
    onClick: () => setTab('energyManagement'),
  }, {
    key: 'decarbonisation',
    text: 'Dekarbonisierung',
    iconProps: { iconName: 'Lightbulb' },
    onClick: () => setTab('decarbonisation'),
  }*/];

  const title = () => {
    let name = properties.parcel?.name || 'Flurstück';

    name += ` (${properties.parcel?.landUse ? ` (${LandUseI18N['en'][properties.parcel.landUse]})` : ''})`;

    return name;
  }

  useEffect(() => {
    if (properties.parcel) {
      const originalCoords = properties.parcel.geometry.coordinates[0][0];
      console.log('Original coordinates:', originalCoords);
      
      // Create a GeoJSON polygon
      const polygon = turf.polygon([originalCoords]);
      console.log('Created polygon:', polygon);
      
      // Calculate area in square meters
      const area = turf.area(polygon);
      console.log('Calculated area:', area);
      
      // Convert to square meters (turf.area returns square meters)
      setArea(area);
    }
  }, [properties]);

  useEffect(() => {
    const call = async () => {
      if (!properties.parcel) {
        return;
      }

      try {
        const result = await userAPI.query(new Query(new EntityQuery(LocalGovernment.type, [], undefined, Filter.geoContainsBoundingBoxFilter(properties.parcel.minLat, properties.parcel.maxLat, properties.parcel.minLon, properties.parcel.maxLon))));

        console.log('Local Government', result);
      } catch (error) {
        console.error(error);
      }

      try {
        const result = await userAPI.query(new Query(new EntityQuery(PostalCode.type, [], undefined, Filter.geoContainsBoundingBoxFilter(properties.parcel.minLat, properties.parcel.maxLat, properties.parcel.minLon, properties.parcel.maxLon))));

        setPostalCode(result.length > 0 ? result[0] : undefined);

        console.log('Postal Code', result);
      } catch (error) {
        console.error(error);
      }

      try {
        const result = await userAPI.query(new Query(new EntityQuery(Area.type, [], undefined, Filter.geoBoundingBoxOverlapFilter(properties.parcel.minLat, properties.parcel.maxLat, properties.parcel.minLon, properties.parcel.maxLon))));

        console.log('Areas', result);
      } catch (error) {
        console.error(error);
      }

      try {
        const result = await userAPI.query(new Query(new EntityQuery(Building.type, [], undefined,
          Filter.geoBoundingBoxOverlapFilter(properties.parcel.minLat, properties.parcel.maxLat, properties.parcel.minLon, properties.parcel.maxLon))));

        console.log('Buildings', result);
      } catch (error) {
        console.error(error);
      }
    };

    call();
  }, [properties]);

  return (
    <div>
      <div>
        <CommandBar
          styles={{ root: { padding: '0px', } }}
          items={_items}
          ariaLabel=""
          farItemsGroupAriaLabel="More actions"
        />
      </div>
      <div className="marginTopL">
        {(() => {
          switch (tab) {
            case 'general': return <div>
              <div>
                <div className="textL">Nutzungsdetails</div>
              </div>
              <div className="displayFlex alignItemsBaseline gapS">
                <div className="textXL fontWeightBold colorPrimary">{properties.parcel?.landUseDetails || '-'}</div>
              </div>
              <div className="marginTopL">
                <div className="textL">Fläche</div>
              </div>
              <div className="displayFlex alignItemsBaseline gapXS">
                <div className="textXL fontWeightBold colorPrimary">{numberFormat.format(area)}</div><div className="textL fontWeightBold colorPrimary">&#13217;</div>
              </div>
            </div>
            case 'usage': return <div>
              <div>
                <Text variant="large">Gebäude</Text>
              </div>
              <div>
                <Text variant="large">Sport</Text>
              </div>
            </div>
            case 'topology': return <div>
              <div>
                <Text variant="large">Höhenprofil</Text>
              </div>
              <div>
                <Text variant="large">Boden</Text>
              </div>
              <div>
                <Text variant="large">Bewuchs</Text>
              </div>
            </div>
            case 'sociodemographics': return <SocioDemographicsProperties object={properties.parcel}></SocioDemographicsProperties>
            case 'poi': return <PoIProperties object={properties.parcel} poisChanged={properties.poisChanged}></PoIProperties>
            case 'weather': return <div>
              <div>
                <Text variant="large">Population</Text>
              </div>
            </div>
            case 'crime': return <div>
              <div className="marginTopL">
                <Text variant="large">Kriminalität</Text>
              </div>
              <div className="marginTopL displayFlex alignItemsBaseline gapS">
                <div className="textXXL fontWeightBold colorPrimaryLight">7.200</div><div className="textXL colorPrimaryLight">Straftaten/100.000 Einwohner</div>
              </div>
              <div className="marginTopM displayFlex alignItemsBaseline gapS">
                <div className="textXL fontWeightBold colorPrimary">3%</div><div className="textL colorPrimary">über Bundesdurchschnitt</div>
              </div>
              <div className="marginTopS displayFlex alignItemsBaseline gapS">
                <div className="textXL fontWeightBold colorPrimary">5%</div><div className="textL colorPrimary">über Durchschnitt Hamburg</div>
              </div>
            </div>
            case 'ownership': return <div>
              <div>
                <Text variant="large">Eigentümer</Text>
              </div>
            </div>          
            case 'decarbonisation': return <div>
              <DecarbonizationProperties object={properties.parcel}></DecarbonizationProperties>
            </div>
          }
        })()}
      </div>
    </div>
  );
}
